import type { DocStatus } from "../api/types";

export const STATUS_LABEL: Record<DocStatus, string> = {
  delivered: "Delivered",
  draft: "Draft",
  superseded: "Superseded",
};

/** initials for the avatar chip */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Date + time, e.g. "18 Jul · 14:32" — the comment-card meta row (rich-comments
 *  §11 Row-2) wants both the day and the time-of-day. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} · ${time}`;
}

export function roleLabel(role: string): string {
  return role
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ── relative time ────────────────────────────────────────────────────────────

/**
 * How a timestamp OLDER THAN A WEEK reads. Not a formatting knob — an editorial
 * register, and the only thing the two surfaces genuinely disagreed on:
 *
 *   · `"coarse"`     keeps counting in relative units — "1w ago", "2mo ago",
 *                    "1y ago". The activity feed wants this: the row's job is
 *                    "how long ago", and it already shows the absolute date in
 *                    the `<time title>`.
 *   · `"short-date"` hands off to an absolute short date — "1 May". A
 *                    notification row is a thing you act on; past a week, "10d
 *                    ago" is less useful than the day it happened.
 */
export type RelativeTail = "coarse" | "short-date";

/**
 * Compact "time ago" phrasing in the reading-room register: terse, no seconds.
 *
 * This is the SINGLE implementation. Until 2026-07-30 the app carried THREE:
 * `notifications/relTime.ts` and `lib/activity.ts` both exported a function
 * literally named `relativeTime`, with different thresholds AND different
 * arithmetic, plus `formatDate`/`formatDateTime` above on raw `toLocale*`.
 * `relTime.ts` justified itself with "the ecosystem i18nz/chronoz relative-time
 * formatters are Zig" — that was FALSE: `@norkron/web-datetime` is TypeScript
 * and owns exactly this. It is not composed here yet only because it is not
 * resolvable from this app; see the handoff note at the bottom of this comment.
 *
 * ARITHMETIC IS `Math.round`, NOT `Math.floor` — a deliberate choice, three
 * reasons. (1) It fixes a live bug: the old `notifications/relTime.ts` floored
 * minutes, so 45–59 seconds rendered as the nonsense "0m ago". (2) 2h55m reads
 * more truthfully as "3h ago" than as "2h ago". (3) It is what
 * `Intl.RelativeTimeFormat`-based `web-datetime.formatRelative` does (measured:
 * `value = sign * Math.round(abs / unit)`), so adopting the lib later is a
 * smaller, verifiable diff instead of a SECOND change to these strings.
 *
 * A FUTURE timestamp is clock skew, never a prediction — it clamps to "just
 * now" rather than reading "in 5m". An unparseable timestamp is `""` so the
 * caller renders nothing rather than "Invalid Date".
 *
 * `now` accepts a `Date` or epoch-ms; the two old copies split on exactly that
 * and it was never a real difference.
 *
 * ⚠️ WAVE-4 HANDOFF (`@norkron/web-datetime`, measured 2026-07-30 against
 * `dist/index.js` v0.1.8). `formatRelative(nowInstant, thenInstant, {locale:
 * "en", style: "narrow"})` agrees with this function on 5m/3h/2d/2mo but
 * DISAGREES on four buckets: sub-45s → "10s ago"/"44s ago" not "just now";
 * 10d → "10d ago" (the lib has no week unit — it jumps days→months at 30d);
 * 400d → "last yr." not "1y ago"; a future timestamp → "in 5m". Swapping it in
 * therefore changes user-visible copy and needs those four decided, not just an
 * alias. The full table is pinned in `format.test.ts`, so the swap can be
 * diffed against a golden rather than eyeballed.
 */
export function relativeTime(
  iso: string,
  now: Date | number = Date.now(),
  tail: RelativeTail = "coarse",
): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const nowMs = typeof now === "number" ? now : now.getTime();

  const sec = Math.max(0, Math.round((nowMs - then) / 1000));
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;

  if (tail === "short-date") {
    return new Date(then).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
  if (day < 30) return `${Math.round(day / 7)}w ago`;
  if (day < 365) return `${Math.round(day / 30)}mo ago`;
  return `${Math.round(day / 365)}y ago`;
}
